/**
 * ScenarioComparisonBlock - Compare different scenarios
 */

import React from 'react'
import { ScenarioComparisonBlock as ScenarioComparisonBlockType } from '../../../../lib/cockpit/schemas'

interface ScenarioComparisonBlockProps {
  data: ScenarioComparisonBlockType['data']
  blockId: string
  compact?: boolean
}

function ScenarioComparisonBlock({ data, blockId, compact }: ScenarioComparisonBlockProps) {
  const { scenarios, axis_id } = data

  if (!scenarios || scenarios.length === 0) {
    return (
      <div className="text-gray-500 dark:text-gray-400 text-sm italic">
        No scenarios to compare
      </div>
    )
  }

  if (compact) {
    return (
      <div className="flex gap-2 overflow-x-auto pb-2">
        {scenarios.slice(0, 3).map((scenario) => (
          <div
            key={scenario.id}
            className="flex-shrink-0 p-2 rounded-lg bg-gray-50 dark:bg-gray-800 min-w-[120px]"
          >
            <div className="font-medium text-sm text-gray-900 dark:text-white truncate">
              {scenario.name}
            </div>
            {scenario.probability !== undefined && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {(scenario.probability * 100).toFixed(0)}% likely
              </div>
            )}
          </div>
        ))}
        {scenarios.length > 3 && (
          <div className="flex-shrink-0 p-2 flex items-center text-gray-400 text-sm">
            +{scenarios.length - 3} more
          </div>
        )}
      </div>
    )
  }

  // Collect all unique metrics across scenarios
  const allMetrics = new Set<string>()
  scenarios.forEach((s) => {
    s.outcomes?.forEach((o) => allMetrics.add(o.metric))
  })
  const metrics = Array.from(allMetrics)

  return (
    <div className="space-y-4">
      {axis_id && (
        <div className="text-xs text-gray-500 dark:text-gray-500">
          Scenario Axis: {axis_id}
        </div>
      )}

      {/* Scenario cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {scenarios.map((scenario) => (
          <div
            key={scenario.id}
            className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-start justify-between mb-3">
              <h4 className="font-semibold text-gray-900 dark:text-white">{scenario.name}</h4>
              {scenario.probability !== undefined && (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  {(scenario.probability * 100).toFixed(0)}%
                </span>
              )}
            </div>

            {scenario.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {scenario.description}
              </p>
            )}

            {scenario.outcomes && scenario.outcomes.length > 0 && (
              <div className="space-y-2">
                <div className="text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                  Outcomes
                </div>
                {scenario.outcomes.map((outcome, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-gray-600 dark:text-gray-400">{outcome.metric}</span>
                    <span className="font-mono text-gray-900 dark:text-white">
                      {String(outcome.value)}
                      {outcome.unit && (
                        <span className="text-gray-500 dark:text-gray-500 ml-1">{outcome.unit}</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Comparison table if there are common metrics */}
      {metrics.length > 0 && scenarios.length >= 2 && (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="py-2 px-3 text-left font-medium text-gray-500 dark:text-gray-400">
                  Metric
                </th>
                {scenarios.map((s) => (
                  <th key={s.id} className="py-2 px-3 text-left font-medium text-gray-900 dark:text-white">
                    {s.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {metrics.map((metric) => (
                <tr key={metric} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="py-2 px-3 text-gray-600 dark:text-gray-400">{metric}</td>
                  {scenarios.map((s) => {
                    const outcome = s.outcomes?.find((o) => o.metric === metric)
                    return (
                      <td key={s.id} className="py-2 px-3 font-mono text-gray-900 dark:text-white">
                        {outcome ? (
                          <>
                            {String(outcome.value)}
                            {outcome.unit && (
                              <span className="text-gray-500 dark:text-gray-500 ml-1">{outcome.unit}</span>
                            )}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default ScenarioComparisonBlock


